import { useState, useEffect } from "react"
import { View, StyleSheet, Image, ViewStyle } from "react-native"

import { colors } from "@/theme"

import { Text } from "./Text"
import { parsePlateIntoParts } from "../utils/plateUtils"

interface NumberPlateVisualProps {
  numberPlate: string
  sizeMode?: "normal" | "compact" | "tiny"
  showRedBand?: boolean
  style?: ViewStyle
  plateBackgroundColor?: string
  inputBackgroundColor?: string
  textColor?: string
  fontSize?: number // Optional font size override
}

/**
 * Display-only German license plate visual component
 * Shows an authentic German plate design with red logo band, inspection sticker, and proper typography
 */
export function NumberPlateVisual({
  numberPlate,
  sizeMode = "compact",
  showRedBand = true,
  style,
  plateBackgroundColor = colors.platePhysicalDark, // Dark gray default (original)
  inputBackgroundColor = colors.platePhysicalMid, // Darker gray default (original)
  textColor = colors.surface, // White text default (original)
  fontSize: fontSizeOverride,
}: NumberPlateVisualProps) {
  const [part1, setPart1] = useState("")
  const [part2, setPart2] = useState("")
  const [part3, setPart3] = useState("")

  useEffect(() => {
    if (numberPlate) {
      const parsedParts = parsePlateIntoParts(numberPlate)
      setPart1(parsedParts[0].toUpperCase())
      setPart2(parsedParts[1].toUpperCase())
      setPart3(parsedParts[2].toUpperCase())
    }
  }, [numberPlate])

  const plateFontFamily = "Kraftfahrzeugfont"

  // Size-specific adjustments
  const baseFontSize = sizeMode === "tiny" ? 8 : sizeMode === "compact" ? 10 : 30
  const fontSize = fontSizeOverride ?? baseFontSize // Use override if provided
  const lineHeight = fontSize // Match lineHeight to fontSize for proper centering
  const paddingHorizontal = sizeMode === "tiny" ? 1 : sizeMode === "compact" ? 2 : 5
  // Scale seal and sticker elements proportionally with fontSize for consistency
  const sealContainerWidth = fontSize * 1.5 // Scales with font size
  const stickerGapWidth = fontSize * 1.0 // Scales with font size

  // Special OHNE handling
  if (part1.toLowerCase() === "ohne") {
    return (
      <View style={[styles.wrapper, style]}>
        <View style={[styles.plateContainer, { backgroundColor: plateBackgroundColor }]}>
          {showRedBand && (
            <View style={styles.redBand}>
              <Image
                source={require("@assets/images/Logo white - transparent.png")}
                style={styles.redBandLogo}
                resizeMode="contain"
              />
            </View>
          )}
          <View style={[styles.inputAreaContainer, { backgroundColor: plateBackgroundColor }]}>
            <View style={styles.ohneContainer}>
              <Text style={[styles.ohneText, { fontSize: fontSize * 2, color: textColor }]}>
                OHNE KENNZEICHEN
              </Text>
            </View>
          </View>
        </View>
      </View>
    )
  }

  return (
    <View style={[styles.wrapper, style]}>
      <View style={[styles.plateContainer, { backgroundColor: plateBackgroundColor }]}>
        {showRedBand && (
          <View style={styles.redBand}>
            <Image
              source={require("@assets/images/Logo white - transparent.png")}
              style={styles.redBandLogo}
              resizeMode="contain"
            />
          </View>
        )}
        <View style={[styles.inputAreaContainer, { backgroundColor: plateBackgroundColor }]}>
          {/* Part 1: City Code */}
          <Text
            style={[
              styles.inputField,
              styles.part1,
              {
                backgroundColor: inputBackgroundColor,
                color: textColor,
                fontFamily: plateFontFamily,
                fontSize: fontSize,
                lineHeight: lineHeight,
                paddingHorizontal: paddingHorizontal,
              },
            ]}
          >
            {part1}
          </Text>

          {/* Seal and Dot */}
          <View style={[styles.sealContainer, { width: sealContainerWidth }]}>
            <Image
              source={require("@assets/images/Plakette.png")}
              style={styles.sealImage}
              resizeMode="contain"
            />
            <View style={styles.sealDot} />
          </View>

          {/* Part 2: Letters */}
          <Text
            style={[
              styles.inputField,
              styles.part2,
              {
                backgroundColor: inputBackgroundColor,
                color: textColor,
                fontFamily: plateFontFamily,
                fontSize: fontSize,
                lineHeight: lineHeight,
                paddingHorizontal: paddingHorizontal,
              },
            ]}
          >
            {part2}
          </Text>

          {/* Sticker Gap */}
          <View style={[styles.stickerGap, { width: stickerGapWidth }]} />

          {/* Part 3: Numbers */}
          <Text
            style={[
              styles.inputField,
              styles.part3,
              {
                backgroundColor: inputBackgroundColor,
                color: textColor,
                fontFamily: plateFontFamily,
                fontSize: fontSize,
                lineHeight: lineHeight,
                paddingHorizontal: paddingHorizontal,
              },
            ]}
          >
            {part3}
          </Text>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  inputAreaContainer: {
    alignItems: "center",
    backgroundColor: colors.platePhysicalDark,
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 0,
  },
  inputField: {
    alignSelf: "center",
    backgroundColor: colors.platePhysicalMid,
    borderRadius: 6,
    color: colors.surface, // White text
    paddingVertical: 8,
    textAlign: "center",
  },
  ohneContainer: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    paddingVertical: 20,
  },
  ohneText: {
    color: colors.surface,
    fontWeight: "700",
    letterSpacing: 2,
  },
  part1: {
    flex: 0.33,
    marginLeft: 10,
  },
  part2: {
    flex: 0.22,
  },
  part3: {
    flex: 0.38,
    marginRight: 10,
  },
  plateContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: colors.platePhysicalDark, // Dark gray plate background
    borderWidth: 2,
    borderColor: "rgba(228, 52, 43, 0.8)", // Red border
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "stretch",
    shadowColor: colors.palette.neutral900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    overflow: "hidden",
  },
  redBand: {
    alignItems: "center",
    backgroundColor: colors.primary,
    justifyContent: "center",
    width: "9.6%",
  },
  redBandLogo: {
    height: "70%",
    width: "70%",
  },
  sealContainer: {
    alignItems: "center",
    flexDirection: "column",
    height: "100%",
    justifyContent: "space-around",
  },
  sealDot: {
    aspectRatio: 1,
    backgroundColor: colors.palette.neutral300,
    borderRadius: 500,
    height: "30%",
    width: "30%",
  },
  sealImage: {
    aspectRatio: 1,
    height: "30%",
    width: "30%",
  },
  stickerGap: {
    alignItems: "center",
    height: "100%",
    justifyContent: "center",
  },
  wrapper: {
    alignItems: "center",
    aspectRatio: 520 / 110,
    justifyContent: "center",
    width: "100%",
  },
})
